import axios from 'axios';
import type { BranchSummary, MerchantSummary } from '@/stores/auth';
import { useAuthStore } from '@/stores/auth';

interface RefreshSessionResponse {
  access_token: string;
  refresh_token: string;
  user: {
    id: string;
    phone: string;
    name?: string;
    role: 'merchant_owner' | 'merchant_staff' | 'consumer';
    email?: string | null;
  };
  merchant?: MerchantSummary;
  branches?: BranchSummary[];
}

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

/**
 * Recover or extend the session from POST /auth/refresh.
 *
 * Two token sources, in order of preference:
 * 1. The in-memory refresh token (present after an in-session login).
 * 2. The HttpOnly `dp_refresh` cookie set by the backend on login —
 *    sent via `withCredentials` with an empty body. This is what makes
 *    a full page reload keep the user signed in (MF-073): tokens are
 *    memory-only, so on boot only the cookie can recover the session.
 *
 * Note: on a cross-origin API base (prod api.digipop.com) the cookie
 * additionally requires backend CORS `allow-credentials` for the app
 * origin; the dev deploy proxies same-origin so the cookie just rides.
 */
export async function refreshSessionFromCookie(): Promise<string | null> {
  const refreshToken = useAuthStore.getState().refreshToken;

  try {
    const res = await axios.post<RefreshSessionResponse>(
      `${baseURL}/auth/refresh`,
      refreshToken ? { refresh_token: refreshToken } : {},
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      },
    );

    const authState = useAuthStore.getState();
    authState.setSession({
      access_token: res.data.access_token,
      refresh_token: res.data.refresh_token,
      user: res.data.user,
      merchant: res.data.merchant ?? authState.merchant ?? undefined,
      // Live /auth/refresh returns branches: [] — keep whatever we
      // already have so a silent in-session refresh never wipes the
      // branch picker (TopBar re-syncs from /merchant/branches).
      branches: res.data.branches?.length ? res.data.branches : authState.branches,
    });

    return res.data.access_token;
  } catch {
    useAuthStore.getState().logout();
    return null;
  }
}
